import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToggleService } from '../../../auctions/services/toggle.service';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    ListCards200Response,
    PaymentProfileCardsResponse,
} from '@viewbid/ts-angular-client';
import { CardTypes } from '../../enums/card-types';
import { ProfileService } from 'src/app/viewbid/shared/services/profile.service';
import {
    VbMenuItem,
    VbPaymentBrand,
    VbPaymentChip,
    VbSnackbarService,
} from '@viewbid/ui';
import {
    isCardExpired,
    isCardExpiringSoon,
} from 'src/app/viewbid/shared/utils/payment-utils';

@Component({
    selector: 'vb-user-payment-information',
    templateUrl: './user-payment-information.component.html',
    styleUrls: ['./user-payment-information.component.scss'],
})
export class UserPaymentInformationComponent implements OnInit {
    paymentMethods: PaymentProfileCardsResponse[] = [];
    isLoading: boolean = false;
    private successToastId: number = 0;

    readonly cardMenuItems: VbMenuItem[] = [
        { id: 'remove', label: 'Remove card', danger: true },
    ];

    /** Remove-card confirm (vb-dialog) — replaces the legacy ngx-bootstrap
     *  ConfirmationWindowComponent for this flow. */
    deleteConfirmOpen: boolean = false;
    private pendingDeleteId: string | null = null;

    constructor(
        public toggleService: ToggleService,
        public profileService: ProfileService,
        private translate: TranslateService,
        private auctionCatalogsService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private router: Router,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.getPaymentMethods();
    }

    goToAddCard(): void {
        this.router.navigate(['../add-payment-information'], {
            relativeTo: this.route,
        });
    }

    private async getPaymentMethods(): Promise<void> {
        this.isLoading = true;
        await this.auctionCatalogsService.listCards().subscribe({
            next: (response: ListCards200Response) => {
                this.isLoading = false;
                this.paymentMethods =
                    response.data as PaymentProfileCardsResponse[];
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant('Payments.cardListLoadFailed'),
                );
            },
        });
    }

    deletePaymentCard(customerPaymentProfileId: string): void {
        this.isLoading = true;
        this.auctionCatalogsService
            .deleteCard(customerPaymentProfileId)
            .subscribe({
                next: () => {
                    const isLastCard: boolean =
                        this.paymentMethods.length === 1;
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            isLastCard
                                ? 'Payments.cardDeleteLastCardSuccess'
                                : 'Payments.cardDeleteSuccess',
                        ),
                    );
                    this.isLoading = false;
                    this.paymentMethods = this.paymentMethods.filter(
                        (item: PaymentProfileCardsResponse) =>
                            item.customerPaymentProfileId !==
                            customerPaymentProfileId,
                    );
                    this.profileService.updateUserStore();
                },
                error: () => {
                    this.isLoading = false;
                    this.snackbarService.error(
                        this.translate.instant('Payments.cardDeleteFailed'),
                    );
                },
            });
    }

    /** Maps the API card type onto the vb-payment-method-row brand mark. */
    getCardBrand(cardType?: string): VbPaymentBrand {
        const type: string = (cardType ?? '').toLowerCase();
        if (type === CardTypes.Visa) return 'visa';
        if (type === CardTypes.MasterCard) return 'mastercard';
        return 'generic';
    }

    /** Last four digits of the masked card number (e.g. "XXXX1111" → "1111"). */
    getCardLast4(cardNumber?: string): string {
        return cardNumber ? cardNumber.slice(-4) : '';
    }

    /**
     * The card's own billing address on one line, e.g.
     * "1 Main St, Halifax, NS B3H 0A1"; null (no line) when the API has none.
     */
    getBillingLabel(card: PaymentProfileCardsResponse): string | null {
        const provincePostal: string = [
            card.billingProvince,
            card.billingPostalCode,
        ]
            .filter((part?: string | null) => !!part)
            .join(' ');
        const label: string = [
            card.billingAddress1,
            card.billingCity,
            provincePostal,
        ]
            .filter((part?: string | null) => !!part)
            .join(', ');
        return label || null;
    }

    /** "YYYY-MM" → "MM / YY" (rendered by the row as "EXP MM / YY"). */
    getExpLabel(expirationDate?: string): string {
        if (!expirationDate) return '';
        const [year, month]: string[] = expirationDate.split('-');
        if (!year || !month) return expirationDate;
        return `${month} / ${year.slice(-2)}`;
    }

    /**
     * Status chip: warn "Expired" / "Expires soon" from the shared
     * payment-utils helpers; null otherwise. (No default-card concept
     * exists in the API today, so the neutral "Default" chip is not
     * produced.)
     */
    getCardChip(card: PaymentProfileCardsResponse): VbPaymentChip | null {
        if (isCardExpired(card.expirationDate)) {
            return { label: 'Expired', tone: 'warn' };
        }
        if (isCardExpiringSoon(card.expirationDate)) {
            return { label: 'Expires soon', tone: 'warn' };
        }
        return null;
    }

    onCardMenuSelect(itemId: string, card: PaymentProfileCardsResponse): void {
        if (itemId === 'remove') {
            this.onDeleteMenuClick(card.customerPaymentProfileId);
        }
    }

    onDeleteMenuClick(customerPaymentProfileId: string): void {
        this.pendingDeleteId = customerPaymentProfileId;
        this.deleteConfirmOpen = true;
    }

    closeDeleteConfirm(): void {
        this.deleteConfirmOpen = false;
        this.pendingDeleteId = null;
    }

    onDeleteConfirmed(): void {
        const cardId: string | null = this.pendingDeleteId;
        this.closeDeleteConfirm();
        if (cardId) this.deletePaymentCard(cardId);
    }
}
